import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, Modal, Note } from '@repo/design-system';
import {
  fontMono,
  ink,
  radius,
  rgba,
  shadow,
  space,
  surface,
  tracking,
  typeRole,
} from '@repo/design-tokens';
import type { DroppedDraftSummary, TwinLaunchSummary } from '@repo/frontend-types';

import type { DraftChoiceError } from './useDraftChoice';
import type { DifferenceState } from './useLoadComparison';
import DraftChoiceErrorBanner from './DraftChoiceErrorBanner';
import { whenText } from './draftChoiceText';
import { dateText, font, type ChangesState } from './VersionsModal';
import {
  differenceCaption,
  differenceStatusText,
  draftDropText,
  loadButtonText,
  loadLead,
  loadTitle,
  noDifferenceText,
} from './versionsText';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  p {
    margin: 0;
  }

  .lead {
    font: ${font(typeRole.body1Strong)};
  }

  /* Frame 09's caption: micro-caps mono. Body ink, not the quaternary the frame draws: ink-4 is
     for icons and reads 3.7:1 on white, short of AA (PR #2272; owed Rob's ratification). */
  .caption {
    font: ${font({ ...typeRole.caption2, fontFamily: fontMono })};
    letter-spacing: ${tracking.label};
    text-transform: uppercase;
    color: ${ink[3]};
  }

  .status {
    display: flex;
    align-items: center;
    gap: ${space[2]};
    font: ${font(typeRole.caption1)};
    color: ${ink[3]};

    p {
      flex: 1 1 auto;
      min-width: 0;
    }

    /* Layout only: the line gives way, never the button's label. */
    > button {
      flex: none;
    }
  }

  /* Empty once the rows are in: kept mounted, so the live region is the same node throughout, but
     out of the flow so it adds no gap. */
  .status.is-empty {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
`;

/**
 * Frame 09's difference rows: white glass, the step's glyph and title in the quieter ink. One grid
 * the rows share (`subgrid`), so every row's text starts at the same place whatever its step's name.
 */
const DifferenceList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: auto max-content 1fr;
  row-gap: ${space[2]};

  li {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    align-items: center;
    column-gap: ${space[3]};
    padding: ${space[2]} ${space[3]};
    border-radius: ${radius.lg};
    background: ${rgba(surface[0], 0.55)};
    border: 1px solid ${rgba(surface[0], 0.85)};
    box-shadow: ${shadow.glassSm};
    font: ${font(typeRole.caption1Strong)};
    color: ${ink[1]};
  }

  .row-icon {
    display: inline-flex;
    justify-content: center;
    width: ${space[4]};
    color: ${ink[3]};
  }

  .row-step {
    color: ${ink[3]};
  }

  .row-text {
    min-width: 0;
  }
`;

export interface LoadLaunchModalProps {
  /** The launch to load; null renders nothing. */
  launch: TwinLaunchSummary | null;
  /** Production's launch number (*v4*); null to say production alone. */
  productionVersion: string | null;
  currentEmail: string | null | undefined;
  /** What the launch lacks and has against production; null shows no comparison. */
  difference: DifferenceState | null;
  /** The draft's changes against production, as Versions counts them. */
  draftChanges: ChangesState | null;
  /** When the draft's first unlaunched edit was saved (`draftBasis.editedAt`). */
  draftEditedAt: string | null;
  dropped: DroppedDraftSummary | null;
  droppedReadFailed: boolean;
  /** The dropped-draft read is outstanding: Load waits, so its "replaces" line is never skipped. */
  droppedChecking: boolean;
  busy: boolean;
  error: DraftChoiceError | null;
  onLoad: () => void;
  onCancel: () => void;
  onReload: () => void;
  onRetryDifference: () => void;
}

/**
 * The confirmation before Versions' *Load as Draft* (`twin-details-page-load-launch`, frame 09): which
 * launch it is and who made it, that customers keep seeing production, what the launch lacks and
 * then has against production by step, and that the current draft — counted as Versions counts it —
 * is dropped and can be restored. Coral as the design draws it, though `Modal` keeps coral for
 * irreversible deletes: owed Rob's ratification, as Discard's is.
 *
 * The comparison and the count inform the choice; they never gate it — Load stays pressable while
 * either is loading or failed. Dismissible until the load runs, when every dismissal route is held
 * so the page cannot be edited under the swap. Its dropped-draft lines are `DiscardDraftModal`'s.
 *
 * Footer order follows card 08's pairing: the secondary action first, the primary last.
 */
const LoadLaunchModal: React.FC<LoadLaunchModalProps> = ({
  launch,
  productionVersion,
  currentEmail,
  difference,
  draftChanges,
  draftEditedAt,
  dropped,
  droppedReadFailed,
  droppedChecking,
  busy,
  error,
  onLoad,
  onCancel,
  onReload,
  onRetryDifference,
}) => {
  // *Try again* stays mounted, busy, while its read runs, so focus is not dropped to the page — as
  // Versions' own (review, 2026-10-16).
  const [retrying, setRetrying] = useState(false);
  if (!launch) return null;
  const bi = launch.biOutcome;
  const retryRunning = retrying && difference?.state === 'loading';
  const sameAsProduction = difference?.state === 'ready' && difference.rows.length === 0;
  const date = dateText(launch.completedAt ?? launch.launchedAt);
  const status =
    difference?.state === 'loading' || difference?.state === 'failed'
      ? differenceStatusText(launch, difference.state)
      : '';
  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!busy}
      title={loadTitle(launch)}
      tone="coral"
      width="md"
      icon={<Icon name="clock-rotate-left" variant="solid" size="xs" />}
      actions={
        <>
          <Button type="button" variant="frosted" onClick={onCancel} disabled={busy}>
            Keep My Draft
          </Button>
          <Button
            type="button"
            variant="destructive"
            iconLeft={<Icon name="clock-rotate-left" variant="solid" size="xs" />}
            onClick={onLoad}
            loading={busy}
            disabled={droppedChecking}
          >
            {loadButtonText(launch)}
          </Button>
        </>
      }
    >
      <Body>
        <p className="lead">{loadLead({ launch, date, productionVersion, currentEmail })}</p>
        {difference && !sameAsProduction && (
          <p className="caption">{differenceCaption(launch, productionVersion)}</p>
        )}
        {difference?.state === 'ready' &&
          (difference.rows.length > 0 ? (
            <DifferenceList aria-label="Compared with production">
              {difference.rows.map(row => (
                <li key={row.step}>
                  <span className="row-icon" aria-hidden="true">
                    <Icon name={row.icon} variant="solid" size="xs" />
                  </span>
                  <span className="row-step">{row.label}</span>
                  <span className="row-text">{row.text}</span>
                </li>
              ))}
            </DifferenceList>
          ) : (
            <p>{noDifferenceText(launch)}</p>
          ))}
        {/* One polite region whose text changes: working it out, then failed, then empty. */}
        {difference && (
          <div className={status ? 'status' : 'status is-empty'}>
            <p role="status">{status}</p>
            {(difference.state === 'failed' || retryRunning) && (
              <Button
                type="button"
                variant="frosted"
                size="sm"
                loading={retryRunning}
                onClick={() => {
                  setRetrying(true);
                  onRetryDifference();
                }}
              >
                Try again
              </Button>
            )}
          </div>
        )}
        <Note tone="butter" icon={<Icon name="pen" variant="solid" size="xs" />}>
          {draftDropText(draftChanges, draftEditedAt)}
        </Note>
        {droppedChecking && <p role="status">Checking for a dropped draft…</p>}
        {dropped && <p>This replaces the draft you dropped {whenText(dropped.droppedAt)}.</p>}
        {droppedReadFailed && (
          <Note tone="butter">
            We couldn&apos;t check for a dropped draft, so this may replace one.
          </Note>
        )}
        {bi && (bi.outcome === 'failed' || bi.outcome === 'not_updated') && (
          <Note tone="butter">
            BI was not updated by this launch{bi.reason ? `: ${bi.reason}` : '.'}
          </Note>
        )}
        {error && <DraftChoiceErrorBanner error={error} onReload={onReload} />}
      </Body>
    </Modal>
  );
};

export default LoadLaunchModal;
